import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { 
  Speed as SpeedIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

export interface ExecutionTimeDisplayProps {
  executionTime: number;
  status: 'success' | 'warning' | 'error' | 'loading';
  showIcon?: boolean;
  size?: 'small' | 'medium' | 'large';
  animated?: boolean;
}

const ExecutionTimeDisplay: React.FC<ExecutionTimeDisplayProps> = ({
  executionTime,
  status,
  showIcon = true,
  size = 'medium',
  animated = true
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          color: (executionTime < 2 ? 'success' : executionTime < 5 ? 'warning' : 'error') as 'success' | 'warning' | 'error',
          icon: <SuccessIcon />,
          text: 'Fast'
        };
      case 'warning':
        return {
          color: 'warning' as const,
          icon: <WarningIcon />,
          text: 'Slow'
        };
      case 'error':
        return {
          color: 'error' as const,
          icon: <ErrorIcon />,
          text: 'Failed'
        };
      case 'loading':
        return {
          color: 'info' as const,
          icon: <SpeedIcon />,
          text: 'Processing'
        };
      default:
        return {
          color: 'info' as const,
          icon: <SpeedIcon />,
          text: 'Unknown'
        };
    }
  };

  const config = getStatusConfig();

  const getSizeProps = () => {
    switch (size) {
      case 'small':
        return { 
          fontSize: '0.75rem', 
          iconSize: 16, 
          padding: '2px 8px',
          chipSize: 'small' as const
        };
      case 'large':
        return { 
          fontSize: '1rem', 
          iconSize: 24, 
          padding: '6px 12px',
          chipSize: 'medium' as const
        };
      default:
        return { 
          fontSize: '0.875rem', 
          iconSize: 20, 
          padding: '4px 10px',
          chipSize: 'small' as const
        };
    }
  };

  const sizeProps = getSizeProps();

  const formatExecutionTime = (time: number) => {
    if (status === 'error') return 'Failed';
    if (status === 'loading') return '...';
    if (time < 1) return `${(time * 1000).toFixed(0)}ms`;
    return `${time.toFixed(1)}s`;
  };

  const getPerformanceLabel = (time: number) => {
    if (status === 'error' || status === 'loading') return '';
    if (time < 2) return 'Fast';
    if (time < 5) return 'Good';
    return 'Slow';
  };

  return (
    <Box 
      display="flex" 
      alignItems="center" 
      gap={1}
      sx={{
        animation: animated && status === 'success' ? 'fadeIn 200ms ease-out' : 'none',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(-4px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {showIcon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: `${config.color}.main`,
          }}
        >
          {config.icon}
        </Box>
      )}
      
      <Typography
        variant="body2"
        color={`${config.color}.main`}
        sx={{
          fontWeight: 600,
          fontSize: sizeProps.fontSize,
        }}
      >
        {formatExecutionTime(executionTime)}
      </Typography>

      {status === 'success' && executionTime > 0 && (
        <Chip
          label={getPerformanceLabel(executionTime)}
          size={sizeProps.chipSize}
          color={config.color}
          sx={{
            fontSize: sizeProps.fontSize,
            height: 'auto',
            padding: sizeProps.padding,
            '& .MuiChip-label': {
              fontSize: sizeProps.fontSize,
              fontWeight: 500,
            },
          }}
        />
      )}
    </Box>
  );
};

export default ExecutionTimeDisplay;
